import { useEffect, useMemo, useRef, useState } from "react";

import CellSaveStatusIndicator from "./CellSaveStatusIndicator";
import SelectMenu from "./SelectMenu";

const getSelectionSignature = (selection, isMulti) => {
  const getValue = (item) =>
    String(item && typeof item === "object" ? item.value : (item ?? ""));

  return isMulti
    ? [...(selection ?? [])].map(getValue).sort().join("|")
    : getValue(selection);
};

/**
 * Inline select that immediately commits changes while retaining the user's
 * selected value until refreshed server data arrives.
 */
export default function InlineSelectCell({
  ariaLabel,
  disabled = false,
  disableWhileSaving = true,
  isClearable = false,
  isMulti = false,
  name,
  onCommit,
  options,
  saveStatus = "idle",
  value,
  parentClass = "editable-table-select",
  menuPortalTarget,
  placeholder,
}) {
  const valueSignature = getSelectionSignature(value, isMulti);
  const selectedOption = useMemo(() => {
    if (isMulti) {
      const selectedValues = new Set((value ?? []).map(String));
      return options.filter((option) =>
        selectedValues.has(String(option.value)),
      );
    }

    return options.find((option) => option.value === value) ?? null;
  }, [isMulti, options, value]);
  const [draftOption, setDraftOption] = useState(selectedOption);
  const previousValueSignature = useRef(valueSignature);
  const pendingValueSignature = useRef(null);
  const draftSignature = getSelectionSignature(draftOption, isMulti);

  useEffect(() => {
    if (pendingValueSignature.current !== null) {
      // Only the newest committed value can confirm the draft. Ignore older
      // or out-of-order parent refreshes regardless of save-indicator timing.
      if (pendingValueSignature.current === valueSignature) {
        pendingValueSignature.current = null;
        previousValueSignature.current = valueSignature;
      }
      return;
    }

    if (previousValueSignature.current === valueSignature) return;

    previousValueSignature.current = valueSignature;
    setDraftOption(selectedOption);
  }, [selectedOption, valueSignature]);

  const isSaving = saveStatus === "saving";

  return (
    <div className="flex min-w-0 items-center gap-1">
      <SelectMenu
        handleInputChange={(event) => {
          const nextOption = isMulti ? (event.target.value ?? []) : event.target.value;
          const nextSignature = getSelectionSignature(nextOption, isMulti);

          if (nextSignature === draftSignature) return;

          // Keep the new selection visible while the parent refreshes its row.
          pendingValueSignature.current = nextSignature;
          setDraftOption(nextOption);
          onCommit(nextOption);
        }}
        name={name}
        options={options}
        value={draftOption}
        isMulti={isMulti}
        isClearable={isClearable}
        placeholder={placeholder}
        parentClass={parentClass}
        ariaLabel={ariaLabel}
        isDisabled={disabled || (disableWhileSaving && isSaving)}
        menuPortalTarget={
          menuPortalTarget ??
          (typeof document !== "undefined" ? document.body : undefined)
        }
      />
      {!disabled ? <CellSaveStatusIndicator status={saveStatus} /> : null}
    </div>
  );
}
